/**
 * DashShell.tsx
 *
 * Layout shell used by every customer-area page. Provides:
 *   - Aurora background + glass styles (via styles.ts)
 *   - Persistent left sidebar (DashSidebar)
 *   - Page header (date eyebrow + title + optional CTA on the right)
 *   - Max-width content container that's offset to the right of the sidebar
 *   - Mobile drawer (hamburger toggle)
 *   - Lightweight in-shell badge fetch so the sidebar can show open-ticket
 *     counts everywhere without each page passing them in.
 */
import React, { useEffect, useState } from 'react';
import { dashStyles } from './styles';
import DashSidebar from './DashSidebar';

interface Props {
  title: string;
  subtitle?: string;
  eyebrow?: string;            // small uppercase label above the title, defaults to today's date
  pulse?: boolean;             // animated dot before eyebrow (default true)
  right?: React.ReactNode;     // top-right slot (e.g. status pill, CTA button)
  children: React.ReactNode;
}

const apiBase = process.env.REACT_APP_API_URL || '';

const DashShell: React.FC<Props> = ({ title, subtitle, eyebrow, pulse = true, right, children }) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [badges, setBadges] = useState<{ openTickets?: number; expiringDomains?: number }>({});

  const eyebrowText = eyebrow || new Date().toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric',
  });

  // Fetch a lightweight badge feed once. Best-effort; we don't block the UI.
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    fetch(`${apiBase}/api/dashboard/summary`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(j => {
        if (j?.success && j?.data) {
          setBadges({
            openTickets: j.data.support?.open_tickets || 0,
            expiringDomains: j.data.services?.domains?.expiring_soon || 0,
          });
        }
      })
      .catch(() => { /* silent — badges are a nicety */ });
  }, []);

  return (
    <div className="dash-root relative min-h-screen overflow-hidden text-slate-800">
      <style>{dashStyles}</style>

      {/* Aurora background */}
      <div aria-hidden className="aurora-bg">
        <div className="aurora aurora-1" />
        <div className="aurora aurora-2" />
        <div className="aurora aurora-3" />
        <div className="grain" />
      </div>

      {/* Sidebar (desktop persistent + mobile drawer) */}
      <DashSidebar
        badges={badges}
        isMobileOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      />

      {/* Page content — pushed right of sidebar on lg+ */}
      <div className="relative lg:pl-60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Mobile hamburger */}
          <button
            onClick={() => setDrawerOpen(true)}
            className="lg:hidden mb-4 inline-flex items-center gap-2 text-sm text-slate-700 hover:text-slate-900 transition"
            aria-label="Open menu"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 6h16M4 12h16M4 18h16" />
            </svg>
            <span>Menu</span>
          </button>

          {/* Page header */}
          <header className="mb-10 fade-up" style={{ animationDelay: '40ms' }}>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 text-xs text-slate-500 uppercase tracking-[0.2em] mb-2">
                  {pulse && <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />}
                  {eyebrowText}
                </div>
                <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900">{title}</h1>
                {subtitle && <p className="text-slate-500 mt-2 text-base">{subtitle}</p>}
              </div>
              {right}
            </div>
          </header>

          {children}
        </div>
      </div>
    </div>
  );
};

export default DashShell;
